import React, { useEffect, useState, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

// Context
const AuthContext = createContext(null);
const ThemeContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// Global 403 response interceptor with debounce
let lastPermissionToast = 0;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const now = Date.now();
      // Debounce: only show toast if 2+ seconds since last one
      if (now - lastPermissionToast > 2000) {
        lastPermissionToast = now;
        toast.error("You do not have permission to perform this action.", {
          duration: 3500,
        });
      }
    }
    return Promise.reject(error);
  }
);

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          login(response.data);
          toast.success(`Welcome, ${response.data.name}!`);
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error("Auth error:", error);
          // Check if this is an access denied error (403)
          if (error.response?.status === 403) {
            // Try to get user email from session data for the mailto link
            let userEmail = "";
            try {
              const sessionRes = await axios.get(
                `https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data?session_id=${sessionId}`
              );
              userEmail = sessionRes.data?.email || "";
            } catch (e) {
              // Ignore - email will be empty
            }
            navigate('/not-authorized', { replace: true, state: { email: userEmail } });
          } else {
            toast.error("Authentication failed. Please try again.");
            navigate('/login', { replace: true });
          }
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Lazy load pages
const Login = React.lazy(() => import("./pages/Login"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Leads = React.lazy(() => import("./pages/Leads"));
const LeadDetail = React.lazy(() => import("./pages/LeadDetail"));
const Contacts = React.lazy(() => import("./pages/Contacts"));
const ContactDetail = React.lazy(() => import("./pages/ContactDetail"));
const Companies = React.lazy(() => import("./pages/Companies"));
const CompanyDetail = React.lazy(() => import("./pages/CompanyDetail"));
const Listings = React.lazy(() => import("./pages/Listings"));
const ListingDetail = React.lazy(() => import("./pages/ListingDetail"));
const Buyers = React.lazy(() => import("./pages/Buyers"));
const BuyerDetail = React.lazy(() => import("./pages/BuyerDetail"));
const Deals = React.lazy(() => import("./pages/Deals"));
const DealDetail = React.lazy(() => import("./pages/DealDetail"));
const Tasks = React.lazy(() => import("./pages/Tasks"));
const TaskCalendar = React.lazy(() => import("./pages/TaskCalendar"));
const Templates = React.lazy(() => import("./pages/Templates"));
const Reports = React.lazy(() => import("./pages/Reports"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Layout = React.lazy(() => import("./components/Layout"));
const NotAuthorized = React.lazy(() => import("./pages/NotAuthorized"));

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment - process BEFORE any routing
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <React.Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    }>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/not-authorized" element={<NotAuthorized />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/contacts/:id" element={<ContactDetail />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/companies/:id" element={<CompanyDetail />} />
          <Route path="/listings" element={<Listings />} />
          <Route path="/listings/:id" element={<ListingDetail />} />
          <Route path="/buyers" element={<Buyers />} />
          <Route path="/buyers/:id" element={<BuyerDetail />} />
          <Route path="/deals" element={<Deals />} />
          <Route path="/deals/:id" element={<DealDetail />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/calendar" element={<TaskCalendar />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </React.Suspense>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              duration: 4000,
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
export { API, BACKEND_URL };
